import { Formik, Form, Field, FormikProps } from "formik"
import * as Yup from "yup"
import { BillingAddress } from "../../../types/payment"
import bbApi from "../../../api"
import { LightBtn, PrimaryBtn } from "../buttons"
import AddressAutocomplete from "../address-autocomplete"
import { convertGooglePlaceToAddressObj } from "../../utils/address"
import { GooglePlace } from "../../../types"

interface BillingAddressFormProps {
  initialValues: BillingAddress
  orgId: string
  onSuccess: () => void
  onCancel: () => void
}

const addressSchema = Yup.object().shape({
  line1: Yup.string().required("Address line 1 is required"),
  line2: Yup.string(),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  postal_code: Yup.string().required("Postal code is required"),
  country: Yup.string().required("Country is required")
})

const BillingAddressForm = ({
  initialValues,
  orgId,
  onSuccess,
  onCancel
}: BillingAddressFormProps) => {
  const handleSubmit = async (values: BillingAddress) => {
    await bbApi.payment.updateBillingAddress({
      org_id: orgId,
      ...values
    })
    onSuccess()
  }

  const handleAddressSelect = (
    place: GooglePlace | undefined,
    setFieldValue: FormikProps<BillingAddress>["setFieldValue"]
  ) => {
    if (!place) return

    const address = convertGooglePlaceToAddressObj(place)

    if (address.street_address) {
      setFieldValue("line1", address.street_address)
    }
    if (address.city) {
      setFieldValue("city", address.city)
    }
    if (address.state) {
      setFieldValue("state", address.state)
    }
    if (address.zipcode) {
      setFieldValue("postal_code", address.zipcode)
    }
    if (address.country) {
      setFieldValue("country", address.country)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={addressSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting, dirty, setFieldValue }) => (
        <Form className="space-y-6">
          {/* Address Autocomplete */}
          <div className="pb-4">
            <AddressAutocomplete
              name="address-search"
              label="Search Address"
              placeholder="Start typing to search..."
              onChange={place => handleAddressSelect(place, setFieldValue)}
            />
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-neutral-400">
                or enter manually
              </span>
            </div>
          </div>

          {/* Manual Fields */}
          <div>
            <label className="block text-sm text-neutral-400">
              Address Line 1
            </label>
            <Field
              name="line1"
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
            />
            {errors.line1 && touched.line1 && (
              <div className="text-sm text-red-600 mt-1">{errors.line1}</div>
            )}
          </div>

          <div>
            <label className="block text-sm text-neutral-400">
              Address Line 2
            </label>
            <Field
              name="line2"
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-neutral-400">City</label>
              <Field
                name="city"
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
              />
              {errors.city && touched.city && (
                <div className="text-sm text-red-600 mt-1">{errors.city}</div>
              )}
            </div>

            <div>
              <label className="block text-sm text-neutral-400">State</label>
              <Field
                name="state"
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
              />
              {errors.state && touched.state && (
                <div className="text-sm text-red-600 mt-1">{errors.state}</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-neutral-400">
                Postal Code
              </label>
              <Field
                name="postal_code"
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
              />
              {errors.postal_code && touched.postal_code && (
                <div className="text-sm text-red-600 mt-1">
                  {errors.postal_code}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm text-neutral-400">Country</label>
              <Field
                name="country"
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
              />
              {errors.country && touched.country && (
                <div className="text-sm text-red-600 mt-1">
                  {errors.country}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <LightBtn onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </LightBtn>
            <PrimaryBtn
              type="submit"
              disabled={!dirty}
              isLoading={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Address"}
            </PrimaryBtn>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default BillingAddressForm
